import { AnimatePresence, motion } from 'motion/react';
import { X, Moon, Sun, Laptop, Eye, EyeOff, Shield } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentRate: number;
  theme: 'light' | 'dark' | 'system';
  isPrivacyMode: boolean;
  currencies: { code: string; name: string; symbol: string }[];
  onUpdateRate: (rate: number) => Promise<void>;
  localCurrency: string;
  overseasCurrency: string;
  onUpdateLocalCurrency: (currency: string) => Promise<void>;
  onUpdateOverseasCurrency: (currency: string) => Promise<void>;
  onUpdateTheme: (theme: 'light' | 'dark' | 'system') => Promise<void>;
  onUpdatePrivacyMode: (enabled: boolean) => Promise<void>;
}

export function SettingsModal({
  isOpen,
  onClose,
  currentRate,
  theme,
  isPrivacyMode,
  currencies,
  onUpdateRate,
  onUpdateTheme,
  onUpdatePrivacyMode,
  localCurrency,
  overseasCurrency,
  onUpdateLocalCurrency,
  onUpdateOverseasCurrency,
}: SettingsModalProps) {
  const [rateInput, setRateInput] = useState('1');

  useEffect(() => {
    if (isOpen) {
      setRateInput(currentRate.toString());
    }
  }, [isOpen, currentRate]);

  const handleUpdateRate = async () => {
    const rate = parseFloat(rateInput);
    if (!isNaN(rate) && rate > 0) {
      await onUpdateRate(rate);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-[32px] border border-zinc-100 bg-white shadow-2xl transition-colors dark:border-zinc-900 dark:bg-black"
          >
            <div className="space-y-6 overflow-y-auto p-6 sm:p-8">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
                    Settings
                  </h2>
                  <p className="text-xs font-medium text-zinc-500">
                    Manage your preferences
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="flex h-10 w-10 items-center justify-center rounded-2xl bg-zinc-50 text-zinc-400 transition-colors hover:text-zinc-900 dark:bg-zinc-900 dark:hover:text-zinc-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                      Local Currency
                    </label>
                    <select
                      value={localCurrency}
                      onChange={(e) => onUpdateLocalCurrency(e.target.value)}
                      className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-3 text-sm font-bold transition-all outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-zinc-100"
                    >
                      {currencies.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                      Overseas Currency
                    </label>
                    <select
                      value={overseasCurrency}
                      onChange={(e) => onUpdateOverseasCurrency(e.target.value)}
                      className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-3 text-sm font-bold transition-all outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-zinc-100"
                    >
                      {currencies.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                    Exchange Rate (1 {localCurrency} = ? {overseasCurrency})
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="e.g. 1500"
                    className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-5 py-4 text-lg font-bold transition-all outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-zinc-100"
                    value={rateInput}
                    onChange={(e) => setRateInput(e.target.value)}
                  />
                </div>
                <button
                  onClick={handleUpdateRate}
                  className="w-full rounded-[24px] bg-zinc-900 py-2 font-bold text-white shadow-sm shadow-zinc-200 transition-transform active:scale-95 dark:bg-zinc-50 dark:text-zinc-900"
                >
                  Update Rate
                </button>
              </div>

              <div className="h-px bg-zinc-100 dark:bg-zinc-800" />

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                    Appearance
                  </label>
                  <div className="grid grid-cols-3 gap-2 rounded-2xl bg-zinc-100 p-1 dark:bg-zinc-900">
                    {(['light', 'system', 'dark'] as const).map((t) => (
                      <button
                        key={t}
                        onClick={() => onUpdateTheme(t)}
                        className={cn(
                          'flex flex-col items-center justify-center gap-2 rounded-xl py-3 transition-all',
                          theme === t
                            ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-zinc-100'
                            : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-300'
                        )}
                      >
                        {t === 'light' && <Sun className="h-5 w-5" />}
                        {t === 'dark' && <Moon className="h-5 w-5" />}
                        {t === 'system' && <Laptop className="h-5 w-5" />}
                        <span className="text-[10px] font-bold tracking-wider uppercase">
                          {t}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                    Privacy
                  </label>
                  <button
                    onClick={() => onUpdatePrivacyMode(!isPrivacyMode)}
                    className="group flex w-full items-center justify-between rounded-2xl border border-zinc-100 bg-zinc-50 p-4 transition-all active:scale-[0.98] dark:border-zinc-900 dark:bg-zinc-950/50"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          'flex h-10 w-10 items-center justify-center rounded-xl transition-colors',
                          isPrivacyMode
                            ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
                            : 'bg-zinc-200 text-zinc-500 dark:bg-zinc-800'
                        )}
                      >
                        <Shield className="h-5 w-5" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-bold text-zinc-900 dark:text-zinc-100">
                          Hide Balances
                        </p>
                        <p className="text-[10px] text-zinc-500">
                          Blur sensitive numbers
                        </p>
                      </div>
                    </div>
                    {isPrivacyMode ? (
                      <EyeOff className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-zinc-400" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
